import {action, computed, observable, ObservableMap, ObservableSet, makeObservable, runInAction} from 'mobx';
import {ProjectRequester as ProjectRequest, ProjectRequester} from './RequestApi/ProjectRequester'
import {ProcedureRequester} from './RequestApi/ProcedureRequester'
import {generalStore} from '../index'
import {batchOptions} from "../enums";
import {v4 as uuidv4} from 'uuid';
import Utility from "../Utility";
import {
    permissionsEnum,
    responseEnum,
    ROOT_FOLDER_ID,
    runProcedureInEnum,
    trainingAndFieldRoleEnum, contentCreatingRoleEnum, filtersEnum, sortEnum, procedureTypeEnum, procedureNodeTypeEnum,
    accessItemTypeEnum,
} from '../serverClientShared/sharedEnums';
import {DATA_INTERVAL} from "./DataStore";
import sharedUtils from "../serverClientShared/sharedUtils";

const projectRequest = new ProjectRequester();

export const PROJECT_PATH_PREFIX = '/project/:projectId';
const CARD_WIDTH = Utility.isMobile() ? 150 : 245;
const CARD_SIDE_MARGIN = 5;


export class ProjectStore {

    myRolesMap = new ObservableMap();
    currentProject = {};
    currentProjectProceduresMap = new ObservableMap();
    currentProjectMediaFoldersMap = new ObservableMap();
    currentProjectSessionsCountMap = new ObservableMap();
    currentProjectInteractionsMap = new ObservableMap();
    currentFolderId = "root";

    filterSelection = filtersEnum.ALL;
    projectsSortSelection = sortEnum.NAME;
    proceduresSortSelection = sortEnum.NAME;
    currentProjectAnalytics = null;
    myProjectPermissions = [];

    newlyCreatedItem = {
        id: "",
        isFolder: false
    };

    disabledEmailsSet = new ObservableSet();

    mediaLibraryPathArray = [{
        folderId: 'root',
        folderName: generalStore.translate('root')
    }];

    analyticsData = {
        dateFilter: 30,
        procedureFilters: [],
        usersMap: new ObservableMap(),
        usersFilters: [],
        recordingFilter: 'all',
        sortBy: 'StartTimeStamp',
        sortDirection: -1,
        sessions: [],
        pageNumber: 0,
        total: 0,
        batchIndex: 0,
        loading: false
    };

    currentProjectId = "";

    sitePrepTemplates = new Map();

    static CARD_SIDE_MARGIN = CARD_SIDE_MARGIN;
    static CARD_STYLE = {
        width: `${CARD_WIDTH}px`,
        minWidth: `${CARD_WIDTH}px`,
        marginLeft: `${CARD_SIDE_MARGIN}px`,
        marginRight: `${CARD_SIDE_MARGIN}px`
    };

    constructor(dataStore, modalStore) {
        makeObservable(this, {
            myRolesMap: observable,
            currentProject: observable,
            currentProjectId: observable,
            currentProjectProceduresMap: observable,
            currentProjectMediaFoldersMap: observable,
            currentProjectSessionsCountMap: observable,
            currentFolderId: observable,
            filterSelection: observable,
            projectsSortSelection: observable,
            proceduresSortSelection: observable,
            currentProjectAnalytics: observable,
            isOwnerInCurrentProject: computed,
            myProjectPermissions: observable,
            disabledEmailsSet: observable,
            mediaLibraryPathArray: observable,
            analyticsData: observable,
            newlyCreatedItem: observable,
            sitePrepTemplates: observable,
            setCurrentProjectDetails: action,
            getProjectById: action,
            updateMyProjectsAndCollaborates: action,
            filteredProjects: computed,
            sortedProjects: computed,
            sortedProcedures: computed,
            sortedFolders: computed,
            getAllPublishedProcedures: action,
            getAllProcedures: action,
            getAllPublishedProceduresByProjectsIds: action,
            refreshProjectDetails: action,
            refreshProjectSessionsCount: action,
            addProjectFolders: action,
            loadFolderContent: action,
            createProject: action,
            createFolder: action,
            createItem: action,
            renameProject: action,
            updateProject: action,
            deleteProject: action,
            moveFolder: action,
            moveProcedure: action,
            renameFolder: action,
            renameProcedure: action,
            updateMyRoles: action,
            getAnalytics: action,
            isSubFolder: action,
            getProcedureOrFolderName: action,
            createMode: action,
            renameMode: action,
            changeFeature: action,
            removeMode: action,
            duplicateMode: action,
            deleteFolder: action,
            loadProjectInteractions: action,
            getInteractionProp: action,
            getUserDefinedInteractions: action,
            checkAllowed: action,
            checkAllowedCurrentProject: action,
            resetNewlyCreatedItem: action,
            getSitePrepTemplates: action,
            addNewSitePrepTemplate: action,
            checkSameNameProcedure: action,
            buildFolderTree: action,
        });

        this.dataStore = dataStore;
        this.modalStore = modalStore;
    }

    get isOwnerInCurrentProject() {
        return this.currentProject?.ProjectOwner?._id === generalStore.userDetails._id
    }

    setCurrentProjectDetails = async (projectId, forceUpdate = false, setSessionsCount = true) => {
        this.currentProjectSessionsCountMap = new ObservableMap();
        try {
            const currentProject = await this.getProjectById(projectId, forceUpdate);
            this.currentProject = currentProject;
            this.currentProjectId = currentProject.ProjectIdentifier;
            this.getAnalytics(projectId).then(currentProjectAnalytics => this.currentProjectAnalytics = currentProjectAnalytics).catch(() => {});
            if (setSessionsCount) {
                await this.setCurrentProjectSessionsCount(projectId);
            }
        } catch (e) {
            console.error(e);
            throw (e);
        }
    };

    setCurrentProjectSessionsCount = async (projectId) => {
        ProjectRequester.getProcedureSessionsObject(projectId, generalStore.workspace).then(procedureSessionsObject => {
            this.currentProjectSessionsCountMap = new Map(Object.entries(procedureSessionsObject));
        });
    }

    async getProjectById(projectId, forceUpdate = false) {
        const isExpired = Date.now() > this.dataStore.projectsDataExpiration;
        const isExists = this.dataStore.myProjectsMap.has(projectId);
        if (isExpired || !isExists || forceUpdate) {
            const updatedProject = await ProjectRequester.getProject(projectId, generalStore.workspace); // TODO errors
            this.dataStore.set('myProjectsMap', updatedProject.ProjectIdentifier, updatedProject);
        }

        return this.dataStore.myProjectsMap.get(projectId);
    }


    updateMyProjectsAndCollaborates = async (isForced, isPaged) => {
        const projectsIdList = [];
        let myProjectsLength = 0;
        const isExpired = Date.now() > this.dataStore.projectsDataExpiration;
        if (isExpired) {
            this.dataStore.projectsDataExpiration = Date.now() + DATA_INTERVAL;
        } else {
            this.dataStore.myProjectsMap.forEach((project, projectId) => {
                projectsIdList.push(projectId);
            });
        }

        if (isExpired || isForced || !this.dataStore.myProjectsMap.size) {
            const projects = await ProjectRequester.getAllProjects(generalStore.workspace, projectsIdList, this.filterSelection, this.projectsSortSelection, isPaged);
            myProjectsLength = projects.length;
            const ProjectsMap = new ObservableMap(this.dataStore.myProjectsMap);
            projects.forEach(project => {
                ProjectsMap.set(project.ProjectIdentifier, project);
            });
            this.dataStore.setMapInStorage('myProjectsMap', ProjectsMap);
        }

        return myProjectsLength;
    };

    get filteredProjects() {
        let projects = [];
        switch (this.filterSelection) {
            case filtersEnum.SHARED:
                this.myRolesMap.forEach((role, projectId) => {
                    if (!role.isOwner) {
                        const project = this.dataStore.myProjectsMap.get(projectId);
                        if (!!project) {
                            projects.push(project);
                        }
                    }
                });
                break;

            case filtersEnum.OWNED:
                this.myRolesMap.forEach((role, projectId) => {
                    if (role.isOwner) {
                        const project = this.dataStore.myProjectsMap.get(projectId);
                        projects.push(project);
                    }
                });
                break;

            default:
                projects = Array.from(this.dataStore.myProjectsMap.values());
                break;
        }
        return projects;
    }

    get sortedProjects() {
        const filteredProjects = [];
        this.filteredProjects.slice().forEach(project => {
            const newProject = {...project};
            filteredProjects.push(newProject);
        });

        const sortingMethod = this.getSortingFunction(this.projectsSortSelection, "ProjectName", "CreateDate");
        if (sortingMethod) {
            filteredProjects.sort(sortingMethod);
        }
        return filteredProjects;
    }

    get sortedProcedures() {
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectsProceduresMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
        const projectProcedures = Array.from(projectsProceduresMap.values());
        const sortingMethod = this.getSortingFunction(this.proceduresSortSelection, "ProcedureName", "DateCreated");
        if (sortingMethod) {
            projectProcedures.sort(sortingMethod);
        }
        return projectProcedures;
    }

    get sortedFolders() {
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectFolders = this.dataStore.projectsFoldersMap.get(projectId) ? Array.from(this.dataStore.projectsFoldersMap.get(projectId).values()) : [];
        const sortingMethod = this.getSortingFunction(this.proceduresSortSelection, "FolderName", "DateCreated");
        if (sortingMethod) {
            projectFolders.sort(sortingMethod);
        }
        return projectFolders;
    }

    getSortingFunction = (sortingMethodProp, nameProp, createdProp = "CreateDate") => {
        const extractIdTimestamp = (id) => {
            if (typeof id !== 'string' || id.length < 8) return 0;
            const seconds = parseInt(id.substring(0, 8), 16);
            return Number.isFinite(seconds) ? seconds * 1000 : 0;
        };
        const parseToMillis = (val) => {
            if (val == null) return null;
            const num = Number(val);
            if (!isNaN(num) && Number.isFinite(num)) return num;
            const parsed = new Date(val).getTime();
            return isNaN(parsed) ? null : parsed;
        };
        const getCreatedMillis = (doc) => parseToMillis(doc[createdProp]) ?? extractIdTimestamp(doc._id);
        const getModifiedMillis = (doc) => parseToMillis(doc.LastUpdate) ?? extractIdTimestamp(doc._id);
        let sortingMethod;

        // eslint-disable-next-line default-case
        switch (sortingMethodProp) {
            case sortEnum.NAME:
                sortingMethod = (a, b) => Utility.sortByString(a, b, nameProp);
                break;

            case sortEnum.CREATION_DATE:
                sortingMethod = (a, b) => getCreatedMillis(b) - getCreatedMillis(a);
                break;

            case sortEnum.LAST_MODIFIED:
                sortingMethod = (a, b) => getModifiedMillis(b) - getModifiedMillis(a);
                break;
        }
        return sortingMethod;
    };

    checkProjectSameName = (name, projectId = null) => {
        if (!name) {
            return false;
        }
        const userId = generalStore.userDetails._id;
        const allProjects = Array.from(this.dataStore.myProjectsMap.values());
        const sameName = allProjects.filter(project => {
            return (
                project.ProjectIdentifier !== projectId && // dont look at the same project
                project.ProjectOwner?._id === userId && // only look at your project
                project.ProjectName.toLowerCase() === name.toLowerCase() // same name
            )
        });
        return !!sameName.length;
    };

    hasMirroredInSelection = (selectedProceduresIds, selectedFoldersIds) => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) return false;
        const procedures = this.dataStore.projectsProceduresMap.get(projectId);
        for (const id of selectedProceduresIds) {
            if (procedures?.get(id)?.importedFromWorkspace) return true;
        }
        const folders = this.dataStore.projectsFoldersMap.get(projectId);
        for (const id of selectedFoldersIds) {
            if (folders?.get(id)?.importedFromWorkspace) return true;
        }
        return false;
    };

    checkFolderSameName = (name) => {
        if (!name) {
            return false;
        }
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectFolders = Array.from(this.dataStore.projectsFoldersMap.get(projectId).values());
        const sameName = projectFolders.filter(folder => {
            return folder.ParentFolder === this.currentFolderId &&
                folder.FolderName.toLowerCase() === name.toLowerCase()
        });
        return !!sameName.length;
    };

    checkProcedureSameName = (name, procedureId = null) => {
        if (!name) {
            return false;
        }
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectProcedures = Array.from(this.dataStore.projectsProceduresMap.get(projectId).values());
        const sameName = projectProcedures.filter(procedure =>
            procedure.ProcedureIdentifier !== procedureId &&
            procedure.ProcedureName.toLowerCase() === name.toLowerCase() // same name
        );
        return !!sameName.length;
    };

    checkSitePrepTemplateSameName = (name, templateId = null) => {
        if (!name) {
            return false;
        }
        const sameName = Array.from(this.sitePrepTemplates.values()).filter(template =>
            template.TemplateIdentifier !== templateId && // don't look at the same template
            template.ProcedureName.toLowerCase() === name.toLowerCase() // same name
        );
        return !!sameName.length;
    };


    getAllPublishedProcedures = async projectId => {
        // noinspection UnnecessaryLocalVariableJS
        const procedures = await ProcedureRequester.getAllPublishedProcedures(projectId, generalStore.workspace);
        return procedures;
    };

    getAllProcedures = async projectId => {
        // noinspection UnnecessaryLocalVariableJS
        const procedures = await ProcedureRequester.getAllProcedures(projectId, generalStore.workspace);
        return procedures;
    };

    getAllPublishedProceduresByProjectsIds = async (projectsIds) => {
        return await ProcedureRequester.getAllPublishedProceduresByProjectsIds(projectsIds, generalStore.workspace)
    };

    refreshProjectDetails = async (forceUpdate = false) => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) {
            return;
        }
        await this.setCurrentProjectDetails(projectId, forceUpdate);
    };

    refreshProjectSessionsCount = async () => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) {
            return;
        }
        await this.setCurrentProjectSessionsCount(projectId);
    };

    addProjectFolders = async (projectId, forceUpdate = false) => {
        const lastFetched = this.dataStore.projectsFoldersExpirationsMap.get(projectId) || 0;
        const isExpired = Date.now() > lastFetched;
        if (forceUpdate || isExpired || !this.dataStore.projectsFoldersMap.has(projectId)) {
            try {
                const currentProjectFolders = await ProjectRequester.getAllFolders(projectId, generalStore.workspace);
                const projectFoldersMap = new ObservableMap();
                currentProjectFolders.forEach(folder => {
                    projectFoldersMap.set(folder.FolderIdentifier, folder);
                });
                this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
                this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);

            } catch (e) {
                console.error(e);
            }

        }
    };

    fetchCurrentProjectProcedures = async (projectId = this.currentProject?.ProjectIdentifier) => {
        const allowUnpublished = this.checkAllowed(generalStore.workspace, generalStore.userDetails, this.currentProject, permissionsEnum.VIEW_UNPUBLISHED, false);
        const allowPublished = this.checkAllowed(generalStore.workspace, generalStore.userDetails, this.currentProject, permissionsEnum.VIEW_PUBLISHED, true);
        let procedures;
        if (allowUnpublished) {
            procedures = await this.getAllProcedures(projectId);
        } else if (allowPublished) {
            procedures = await this.getAllPublishedProcedures(projectId);
        } else {
            throw new Error('No permission');
        }
        return procedures;
    };

    addProjectProcedures = async (projectId, forceUpdate = true) => {
        // const lastFetched = this.dataStore.projectsProceduresExpirationMap.get(projectId) || 0;
        // const isExpired = Date.now() > lastFetched;
        const promise = this.setProceduresFromDB(projectId);
        await this.modalStore.runAsync(promise, generalStore.translate('couldNotFetchProcedures'), '', generalStore.translate('loadingProcedures') + '...');
        this.dataStore.projectsDataExpiration = Date.now();
    };

    async setProceduresFromDB(projectId) {
        const procedures = await this.fetchCurrentProjectProcedures(projectId);
        const projectProceduresMap = new ObservableMap();
        procedures.forEach(procedure => {
            projectProceduresMap.set(procedure.ProcedureIdentifier, procedure);
        });
        this.dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
        this.dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
    };

    loadFolderContent = async (projectId, nestedRoute, parentId, parentType) => {
        const canSeeUnpublished = this.checkAllowedCurrentProject(permissionsEnum.VIEW_UNPUBLISHED, false);
        try {
            const result = await ProcedureRequester.getFolderLevel(projectId, generalStore.workspace, {
                nestedRoute,
                parentId,
                parentType,
                limit: 10000,
                published: !canSeeUnpublished,
                includePublishedRoles: !canSeeUnpublished,
            });
            const mergeParentId = parentId === projectId ? 'root' : parentId;
            this.mergeFolderLevelIntoMaps(projectId, mergeParentId, result);
            this.dataStore.folderContentMap.set(`${projectId}:${nestedRoute}`, result);
            return result;
        } catch (e) {
            console.error('loadFolderContent failed:', e);
            return null;
        }
    };

    mergeFolderLevelIntoMaps = (projectId, parentId, result) => {
        runInAction(() => {
            const foldersMap = this.dataStore.projectsFoldersMap.get(projectId) || new ObservableMap();
            const incomingFolderIds = new Set();
            for (const folder of (result.folders || [])) {
                if (folder.FolderIdentifier) {
                    incomingFolderIds.add(folder.FolderIdentifier);
                    foldersMap.set(folder.FolderIdentifier, folder);
                }
            }
            for (const [id, folder] of foldersMap) {
                if (folder.ParentFolder === parentId && !incomingFolderIds.has(id)) foldersMap.delete(id);
            }
            this.dataStore.set('projectsFoldersMap', projectId, foldersMap);

            const proceduresMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
            const incomingProcedureIds = new Set();
            for (const proc of (result.items || [])) {
                if (proc.ProcedureIdentifier) {
                    incomingProcedureIds.add(proc.ProcedureIdentifier);
                    proceduresMap.set(proc.ProcedureIdentifier, proc);
                }
            }
            for (const [id, proc] of proceduresMap) {
                if (proc.ParentFolder === parentId && !incomingProcedureIds.has(id)) proceduresMap.delete(id);
            }
            this.dataStore.set('projectsProceduresMap', projectId, proceduresMap);
        });
    };

    getFolderContent = (projectId, nestedRoute) => {
        return this.dataStore.folderContentMap.get(`${projectId}:${nestedRoute}`) || null;
    };

    getCurrentFolderPath = () => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) return null;
        const folderPath = Utility.getPathArray().slice(4).filter((p) => p !== ROOT_FOLDER_ID);
        return {projectId, folderPath, nestedRoute: [projectId, ...folderPath].join('/')};
    };

    refreshCurrentFolderLevel = async () => {
        const path = this.getCurrentFolderPath();
        if (!path) return;
        const {projectId, folderPath, nestedRoute} = path;
        const isRoot = folderPath.length === 0;
        const parentId = isRoot ? projectId : folderPath[folderPath.length - 1];
        const parentType = isRoot ? accessItemTypeEnum.PROJECT : accessItemTypeEnum.FOLDER;
        await this.loadFolderContent(projectId, nestedRoute, parentId, parentType);
    };


    createProject = async (projectName, modalStore) => {
        const hasSameName = this.checkProjectSameName(projectName);
        if (hasSameName) {
            modalStore.showErrorMessage(generalStore.translate('saveSameNameProjectError'));
            return false;
        }

        const firstName = generalStore.userDetails.Name;
        const lastName = generalStore.userDetails.LastName;
        const projectIdentifier = uuidv4();
        const project = {
            ProjectIdentifier: projectIdentifier,
            ProjectName: projectName,
            ProjectContactName: `${firstName} ${lastName}`,
            CreateDate: Date.now(),
            ProjectOwner: generalStore.userDetails._id
        };
        const saveProject = await projectRequest.createOrUpdateNewProject(project, generalStore.workspace);
        const success = !!saveProject;
        if (success) {
            this.dataStore.set('myProjectsMap', saveProject.ProjectIdentifier, saveProject);
        }
        return success ? saveProject.ProjectIdentifier : '';
    };

    createFolder = async (folderName) => {
        const hasSameName = this.checkFolderSameName(folderName);
        if (hasSameName) {
            throw new Error(generalStore.translate('saveSameNameFolderError'));
        }

        const projectId = this.currentProject?.ProjectIdentifier;
        const folder = {
            ProjectRef: projectId,
            FolderIdentifier: uuidv4(),
            FolderName: folderName,
            DateCreated: Date.now(),
            ParentFolder: this.currentFolderId
        };

        const newFolder = await ProjectRequester.createOrUpdateFolder(folder, generalStore.workspace);
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId) || new ObservableMap();
        projectFoldersMap.set(newFolder.FolderIdentifier, folder);
        this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);
        return newFolder.FolderIdentifier;
    };

    createItem = async (procedureName, permittedRoles, procedureType, mediaFile = null, link = null, sitePrepData = null, uploadingMethod = "") => {
        const hasSameName = this.checkProcedureSameName(procedureName);
        if (hasSameName) {
            const e = new Error();
            e.response = {status: responseEnum.DUPLICATE}; // faking a server response
            throw e;
        }
        const procedureId = uuidv4();
        const procedure = {
            ProcedureIdentifier: procedureId,
            ProcedureName: procedureName,
            ProcedureTags: '',
            ProjectRef: this.currentProject?.ProjectIdentifier,
            PreRequiredProcedure: [],
            RequiredTools: [],
            RequiredExperience: [],
            NestedNodes: [],
            ParentFolder: this.currentFolderId,
            PreRequiredProcedures: [],
            ProcedureDescription: '',
            DateCreated: Date.now(),
            IsPublished: false,
            IsProjectPublic: this.currentProject?.IsPublic,
            PermittedTrainingAndFieldRoles: permittedRoles,
            ProcedureType: procedureType,
            MediaProcedureFile: mediaFile,
            ProcedureLink: link,
            ThreeDModelsConnected: this.currentProject?.Default3DModel ? [this.currentProject?.Default3DModel] : [],
            AllowRun: this.currentProject?.Default3DModel ? runProcedureInEnum.BOTH : (procedureType === procedureTypeEnum.REGULAR ? runProcedureInEnum.TWO_D : runProcedureInEnum.BOTH)
        };
        if (procedureType === procedureTypeEnum.REGULAR) {
            procedure.NestedNodes.push({
                Identifier: uuidv4(),
                NodeType: procedureNodeTypeEnum.START,
                ThisNodePosition: {x: window.innerWidth / 2 - 80, y: 150},
                StepHeaderMulti: {"EN-US": ""}
            })
        }
        if (!!uploadingMethod) {
            procedure.UploadStatus = uploadingMethod;
        }

        if (!!sitePrepData) {
            procedure.templateId = sitePrepData.templateId;
            procedure.SitePrepAssignees = sitePrepData.SitePrepAssignees;
            procedure.SitePrepFloorPlan = sitePrepData.SitePrepFloorPlan;
        }

        const newProcedure = await ProcedureRequester.createOrUpdateProcedure(procedure, generalStore.workspace);
        const projectId = newProcedure.ProjectRef;
        const projectProceduresMap = new ObservableMap(this.dataStore.projectsProceduresMap.get(projectId) || []);
        projectProceduresMap.set(procedureId, newProcedure);
        this.dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
        this.dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
        return procedureId;
    };

    adminGetAllProjects = async () => {
        return await ProjectRequester.adminGetAllProjects(generalStore.workspace);
    };


    renameProject = async (projectIdentifier, projectNewName, modalStore) => {
        const hasSameName = this.checkProjectSameName(projectNewName, projectIdentifier);
        if (hasSameName) {
            modalStore.showErrorMessage(generalStore.translate('saveSameNameProjectError'));
            return false;
        }
        const project = {
            ProjectIdentifier: projectIdentifier,
            ProjectName: projectNewName,
        };

        const saveProject = await projectRequest.createOrUpdateNewProject(project, generalStore.workspace);
        const success = !!saveProject;
        if (success) {
            this.dataStore.set('myProjectsMap', saveProject.ProjectIdentifier, saveProject);
        }
        return success;
    };

    updateProject = async (project) => {
        const savedProject = await projectRequest.createOrUpdateNewProject(project, generalStore.workspace);
        const success = !!savedProject;
        if (success) {
            this.dataStore.set('myProjectsMap', savedProject.ProjectIdentifier, savedProject);
        }
        return success;
    };

    deleteProject = async (projectId) => {
        // noinspection UnnecessaryLocalVariableJS
        const success = await projectRequest.deleteProject(projectId, generalStore.workspace);
        if (success) {
            this.dataStore.myProjectsMap.delete(projectId);
        }
        return success;
    };


    moveFolder = async (folderId, projectId, parentFolderId) => {
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId) || new ObservableMap();
        const folder = projectFoldersMap.get(folderId);

        const folderUpdate = {
            FolderIdentifier: folder.FolderIdentifier,
            ParentFolder: parentFolderId,
        };

        const savedFolder = await ProjectRequester.createOrUpdateFolder(folderUpdate, generalStore.workspace);
        projectFoldersMap.set(folderId, savedFolder);
        this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);
    };

    moveProcedure = async (procedureId, projectId, parentFolderId) => {
        const projectProcedureMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
        const procedure = projectProcedureMap.get(procedureId);

        const procedureUpdate = {
            ProcedureIdentifier: procedure.ProcedureIdentifier,
            ParentFolder: parentFolderId,
        };

        const savedProcedure = await ProcedureRequester.createOrUpdateProcedure(procedureUpdate, generalStore.workspace);
        projectProcedureMap.set(procedureId, savedProcedure);
        this.dataStore.set('projectsProceduresMap', projectId, projectProcedureMap);
        this.dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
    };


    renameFolder = async (folderId, folderNewName) => {
        const hasSameName = this.checkFolderSameName(folderNewName, folderId);
        if (hasSameName) {
            throw new Error(generalStore.translate('saveSameNameFolderError'));
        }
        const folder = {
            FolderIdentifier: folderId,
            FolderName: folderNewName,
        };
        // noinspection UnnecessaryLocalVariableJS
        const savedFolder = await ProjectRequester.createOrUpdateFolder(folder, generalStore.workspace);
        const projectId = savedFolder.ProjectRef;
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId) || new ObservableMap();
        projectFoldersMap.set(folderId, savedFolder);
        this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);
    };

    renameProcedure = async (procedureId, procedureNewName, modalStore) => {
        const hasSameName = this.checkProcedureSameName(procedureNewName, procedureId);
        if (hasSameName) {
            modalStore.showErrorMessage(generalStore.translate('saveSameNameProcedureError'));
            return false;
        }
        const procedure = {
            ProcedureIdentifier: procedureId,
            ProcedureName: procedureNewName,
        };

        const savedProcedure = await ProcedureRequester.createOrUpdateProcedure(procedure, generalStore.workspace);
        const projectId = savedProcedure.ProjectRef;
        const projectProceduresMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
        projectProceduresMap.set(procedureId, savedProcedure);
        this.dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
        this.dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
        return true;
    };


    updateMyRoles = async () => {
        const userId = generalStore.userDetails._id;
        this.myRolesMap = new ObservableMap();
        this.dataStore.myProjectsMap.forEach((project) => {
            const owners = project.ProjectOwners || [];
            const isOwner = owners.some((o) => (o._id || o).toString() === userId);
            this.myRolesMap.set(project.ProjectIdentifier, {
                isOwner,
                roleName: isOwner ? generalStore.translate('owner') : null,
            });
        });
    };


    getAnalytics = async projectId => {
        // noinspection UnnecessaryLocalVariableJS
        const projectAnalyticsData = await projectRequest.getProjectAnalytics(projectId, generalStore.workspace);
        return projectAnalyticsData;
    };

    isSubFolder = folderId => {
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId);

        return projectFoldersMap && projectFoldersMap.get(folderId)
    };

    getProcedureFromCurrentProject = (procedureId) => {
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectProceduresMap = this.dataStore.projectsProceduresMap.get(projectId);
        return projectProceduresMap ? projectProceduresMap.get(procedureId) : null;
    };

    buildFolderTree = (procedure) => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) {
            return
        }
        const folderTree = [];
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId);
        if (!projectFoldersMap) return folderTree;

        let currentFolderId = procedure.ParentFolder;

        while (currentFolderId && currentFolderId !== 'root') {
            folderTree.unshift(currentFolderId);
            const folder = projectFoldersMap.get(currentFolderId);
            if (!folder) break;
            currentFolderId = folder.ParentFolder;
        }

        return [...folderTree, procedure.ProcedureIdentifier];
    }

    getAllProceduresFromCurrentProject = () => {
        const projectId = this.currentProject?.ProjectIdentifier;
        return this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
    };

    getAllUsersWithSessionsInProject = async () => {
        const projectId = this.currentProject?.ProjectIdentifier;
        return await ProjectRequester.getAllUsersWithSessionsInProject(projectId, generalStore.workspace);
    };

    getProcedureOrFolderName = id => {
        const projectId = this.currentProject?.ProjectIdentifier;
        if (!projectId) {
            return
        }

        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId);

        const folder = projectFoldersMap ? projectFoldersMap.get(id) : null;
        if (folder) {
            return folder.FolderName
        } else {
            const procedure = this.getProcedureFromCurrentProject(id);
            return procedure ? procedure.ProcedureName : ""
        }
    };

    getSessionsPage = async (dateFilter, procedureFilters, recordingFilter, usersFilters, sortBy, sortDirection, pageNumber, batchNumber) => {

        const searchObj = {
            dateFilter, procedureFilters, recordingFilter, usersFilters, sortBy, sortDirection, pageNumber, batchNumber
        };
        return await ProjectRequester.searchProcedureSessions(this.currentProject?.ProjectIdentifier, searchObj, generalStore.workspace);
    };

    refreshSessionsPage = async () => {
        const searchObj = {...this.analyticsData}
        delete searchObj.sessions;
        delete searchObj.total;
        delete searchObj.usersMap;
        delete searchObj.loading;
        searchObj.batchNumber = batchOptions[searchObj.batchIndex];
        delete searchObj.batchIndex;
        const {
            total,
            sessions
        } = await ProjectRequester.searchProcedureSessions(this.currentProject?.ProjectIdentifier, searchObj, generalStore.workspace);
        if (!!total && !!sessions) {
            this.analyticsData.sessions = sessions;
            this.analyticsData.total = total;
        }
    }

    countProceduresInsideFolderRec = (folderId, counter) => {
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectFolders = this.dataStore.projectsFoldersMap.get(projectId) ? Array.from(this.dataStore.projectsFoldersMap.get(projectId).values()) : [];
        const currentFolderFolders = projectFolders.filter(folder => folder.ParentFolder === folderId);
        const numberOfProceduresInChildFolders = currentFolderFolders.reduce((counter, folder) => {
            return this.countProceduresInsideFolderRec(folder.FolderIdentifier, counter);
        }, 0);
        counter += numberOfProceduresInChildFolders;

        const projectsProceduresMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
        const projectProcedures = Array.from(projectsProceduresMap.values());
        const currentFolderProcedures = projectProcedures.filter(procedure => {
            if (procedure.ParentFolder !== folderId) {
                return false;
            }

            const isContentCreator = generalStore.userDetails.Domains[generalStore.workspace]?.Roles.ContentCreationRole !== contentCreatingRoleEnum.NONE;
            if (this.isOwnerInCurrentProject || isContentCreator) {
                return true;
            }

            const myFieldRole = generalStore.userDetails.Domains[generalStore.workspace]?.Roles.TrainingAndFieldRole;
            if (procedure.PermittedTrainingAndFieldRoles && !procedure.PermittedTrainingAndFieldRoles[myFieldRole]) {
                return false;
            }

            const allowedUnpublished = this.checkAllowedCurrentProject(permissionsEnum.VIEW_UNPUBLISHED, false);
            if (allowedUnpublished) {
                return true;
            }

            const allowedPublished = this.checkAllowedCurrentProject(permissionsEnum.VIEW_PUBLISHED, true);
            // noinspection RedundantIfStatementJS
            if (allowedPublished && procedure.IsPublished) {
                return true;
            }

            return false;

        });
        counter += currentFolderProcedures.length;
        return counter;
    };

    countProceduresInsideFolder = (folderId) => {
        return this.countProceduresInsideFolderRec(folderId, 0);
    };

    getFileDetails = async (projectId, fileId) => {
        return await ProjectRequester.getFileDetails(projectId, fileId, generalStore.workspace);
    };

    getAllProjectFilesAndFolders = async (projectId) => {
        const [files, mediaFolders] = await Promise.all([
            ProjectRequester.getAllProjectFiles(projectId, generalStore.workspace),
            ProjectRequester.getAllProjectMediaFolders(projectId, generalStore.workspace),
        ]);
        return {files, mediaFolders};
    };

    createMode = async () => {
        const projectId = this.currentProject?.ProjectIdentifier;
        // noinspection UnnecessaryLocalVariableJS
        const modes = await ProjectRequester.createMode(projectId, generalStore.workspace, generalStore.translate('newMode'));
        this.currentProject.ThreeDProceduresMods = modes;
        this.dataStore.set('myProjectsMap', this.currentProject?.ProjectIdentifier, this.currentProject);
    };


    renameMode = async (modeIndex, newModeName) => {
        // noinspection UnnecessaryLocalVariableJS
        const modes = await ProjectRequester.renameMode(modeIndex, newModeName, this.currentProject?.ProjectIdentifier, generalStore.workspace);
        this.currentProject.ThreeDProceduresMods = modes;
    };

    changeFeature = async (modeIndex, featureKey, newValue) => {
        // noinspection UnnecessaryLocalVariableJS
        const modes = await ProjectRequester.changeFeature(modeIndex, featureKey, newValue, this.currentProject?.ProjectIdentifier, generalStore.workspace);
        this.currentProject.ThreeDProceduresMods = modes;
        this.dataStore.set('myProjectsMap', this.currentProject?.ProjectIdentifier, this.currentProject);
    };


    removeMode = async (modeIndex) => {
        // noinspection UnnecessaryLocalVariableJS
        const modes = await ProjectRequester.removeMode(modeIndex, this.currentProject?.ProjectIdentifier, generalStore.workspace);
        this.currentProject.ThreeDProceduresMods = modes;
        this.dataStore.set('myProjectsMap', this.currentProject?.ProjectIdentifier, this.currentProject);
    };

    duplicateMode = async (modeIndex) => {
        // noinspection UnnecessaryLocalVariableJS
        const modes = await ProjectRequester.duplicateMode(modeIndex, this.currentProject?.ProjectIdentifier, generalStore.workspace);
        this.currentProject.ThreeDProceduresMods = modes;
        this.dataStore.set('myProjectsMap', this.currentProject?.ProjectIdentifier, this.currentProject);
    };

    deleteFolder = async (folder) => {
        const folderId = folder.FolderIdentifier;
        await this.deleteFolderFromId(folderId);
        const projectId = this.currentProject?.ProjectIdentifier;
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId);
        if (projectFoldersMap) {
            projectFoldersMap.delete(folderId);
        }
        this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
        this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);
    };

    deleteFolderFromId = async folderId => ProjectRequester.deleteFolder(folderId, generalStore.workspace);

    checkAllowed = (workspace, user, project, permissionKey, allowIfPublic) => {
        if (allowIfPublic && project?.IsPublic) {
            return true;
        }
        if (!user || !user.Domains || !workspace) {
            return false;
        }
        const userWorkspaceDetails = user.Domains[workspace];
        return sharedUtils.checkWorkspacePermission(userWorkspaceDetails, permissionKey, generalStore.domainSettings);
    };

    checkAllowedCurrentProject = (permission, allowIfPublic) => {
        return this.checkAllowed(generalStore.workspace, generalStore.userDetails, this.currentProject, permission, allowIfPublic)
    };

    isGuestAndAllowed = () => {
        if (generalStore.userDetails._id) {
            return false;
        }
        const {myProjectPermissions, isOwnerInCurrentProject} = this;
        return this.currentProject?.IsPublic && !myProjectPermissions.length && !isOwnerInCurrentProject;
    };

    createMediaFolder = async (currentFolder, name) => {
        const mediaFolderObj = {
            parentFolderId: currentFolder,
            folderName: name,
            projectId: this.currentProject?.ProjectIdentifier
        };
        const newFolder = await ProjectRequester.createMediaFolder(mediaFolderObj, generalStore.workspace);
        return newFolder;
    };

    resetAnalyticsData = () => this.analyticsData = {
        dateFilter: 30,
        procedureFilters: [],
        usersMap: new ObservableMap(),
        usersFilters: [],
        recordingFilter: 'all',
        sortBy: 'StartTimeStamp',
        sortDirection: -1,
        sessions: [],
        pageNumber: 0,
        total: 0,
        batchIndex: 0,
        loading: false
    }

    addProjectInteractions = interactions => {
        const projectId = this.currentProject?.ProjectIdentifier
        if (!projectId) {
            return
        }

        const interactionMap = new Map(interactions.map(interaction => [interaction.InteractiveStepID, interaction]));
        this.currentProject.interactions = interactionMap;
        this.currentProjectInteractionsMap = interactionMap;
        const retrievedProject = this.dataStore.myProjectsMap.get(projectId);
        retrievedProject.interactions = interactionMap;
        this.dataStore.set('myProjectsMap', projectId, retrievedProject);
    }

    loadProjectInteractions = async (procedure, projectId, loadProjectFromDB) => {
        if (!projectId) {
            return
        }

        try {
            const projectInteractions = await ProjectRequester.getProjectInteractions(generalStore.workspace, projectId);

            if (loadProjectFromDB) {
                await this.setCurrentProjectDetails(projectId);
            }

            if (!!procedure) {
                await this.getUserDefinedInteractions(projectId, procedure);
            }
            this.addProjectInteractions(projectInteractions);
        } catch (e) {
            console.error(e);
        }
    }

    getInteractionProp = (interactionId, key) => {
        const allInteractions = new Map();

        if (this.currentProject?.interactions) {
            this.currentProject.interactions.forEach((v, k) => allInteractions.set(k, v));
        }
        if (this.currentProject?.userDefinedInteractions) {
            this.currentProject.userDefinedInteractions.forEach((v, k) => allInteractions.set(k, v));
        }
        const interaction = allInteractions.get(interactionId);
        if (!interaction) {
            return '';
        }
        return interaction[key] || "";
    }

    getUserDefinedInteractions = async (projectId, procedure) => {
        const userDefinedInteractions = await ProcedureRequester.getUserDefinedInteractions(generalStore.workspace, projectId, [procedure.ProcedureIdentifier, ...(procedure.ThreeDModelsConnected || [])], true);
        this.currentProject.userDefinedInteractions = new Map(userDefinedInteractions.map(interaction => [interaction.InteractiveStepID, interaction]));
        return this.currentProject?.userDefinedInteractions;
    }

    getAllUserDefinedInteractions = async (projectId, procedureIds) => {
        const userDefinedInteractions = await ProcedureRequester.getUserDefinedInteractions(generalStore.workspace, projectId, procedureIds, true);
        this.currentProject.userDefinedInteractions = new Map(userDefinedInteractions.map(interaction => [interaction.InteractiveStepID, interaction]));
        return this.currentProject?.userDefinedInteractions;
    }

    duplicateFolder = async (importedFromWorkspace, folderId) => {
        return await ProjectRequester.duplicateFolder(importedFromWorkspace, folderId, this.currentProject?.ProjectIdentifier, generalStore.workspace);
    }

    resetNewlyCreatedItem = () => this.newlyCreatedItem = {
        id: "",
        isFolder: false
    }

    getSitePrepTemplates = async () => {
        try {
            const templates = await ProjectRequester.getSitePrepTemplates(generalStore.workspace, this.currentProject?.ProjectIdentifier);
            this.sitePrepTemplates = new Map(templates?.map(template => [template.TemplateIdentifier, template]) || []);
        } catch (e) {
            console.error(e)
        }
    }

    addNewSitePrepTemplate = async (templateJson) => {
        templateJson.TemplateIdentifier = uuidv4();

        const updatedTemplate = await ProjectRequester.createOrUpdateSitePrepTemplate(generalStore.workspace, templateJson);
        if (!updatedTemplate) {
            throw new Error('updatedTemplate not returned from server')
        } else this.sitePrepTemplates.set(updatedTemplate.TemplateIdentifier, updatedTemplate);

        //TODO add to data store, make updates a part of data poll
    }

    editSitePrepTemplate = async (templateJson) => {
        const updatedTemplate = await ProjectRequester.createOrUpdateSitePrepTemplate(generalStore.workspace, templateJson);
        if (!updatedTemplate) {
            throw new Error('updatedTemplate not returned from server')
        } else this.sitePrepTemplates.set(updatedTemplate.TemplateIdentifier, updatedTemplate);
    }

    deleteSitePrepTemplate = async templateId => {
        try {
            await ProjectRequester.deleteSitePrepTemplate(generalStore.workspace, templateId);
            this.sitePrepTemplates.delete(templateId);
            return true
        } catch (e) {
            console.error(e);
            return false
        }
    }

    checkSameNameProcedure = async trimmed => {
        return await ProjectRequester.checkSameNameProcedure(trimmed, this.currentProject?.ProjectIdentifier, generalStore.workspace);
    }

}
